import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useSeo } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", email: "", page: "", details: "" };

export default function Corrections() {
    useSeo({
        title: "Report a Correction — HALTONS.CA",
        description:
            "Report a factual error on HALTONS.CA. Corrections are reviewed against supplied source material.",
        path: "/corrections",
    });

    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await axios.post(`${API}/corrections`, form);
            setSubmitted(true);
            setForm(initialForm);
            toast.success("Thank you — your correction report has been received.");
        } catch (err) {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main data-testid="corrections-page">
            <section className="border-b border-border bg-navy text-ivory">
                <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
                    <h1 className="font-serif-display text-4xl font-bold sm:text-5xl">
                        Report a Correction
                    </h1>
                    <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ivory/75 md:text-base">
                        HALTONS.CA profiles are built from supplied information. If you believe
                        something is factually wrong, tell us below — reports are reviewed against the
                        source material.
                    </p>
                </div>
            </section>

            <section className="mx-auto max-w-2xl px-4 py-14 sm:px-6">
                {submitted ? (
                    <div className="border border-teal/40 bg-teal/5 p-8" data-testid="correction-success">
                        <h2 className="font-serif-display text-xl font-semibold text-navy">
                            Thank you
                        </h2>
                        <p className="mt-3 text-sm leading-relaxed text-foreground/90">
                            Your correction report has been received and will be reviewed. Profiles may
                            be updated as verified information is supplied.
                        </p>
                        <button
                            type="button"
                            data-testid="correction-another-button"
                            onClick={() => setSubmitted(false)}
                            className="mt-5 text-sm font-semibold text-teal link-underline"
                        >
                            Submit another report
                        </button>
                    </div>
                ) : (
                    <form onSubmit={onSubmit} data-testid="correction-form" className="space-y-5">
                        <div>
                            <label htmlFor="correction-page" className="text-xs font-semibold uppercase tracking-widest text-navy">
                                Page or profile concerned <span className="text-teal">*</span>
                            </label>
                            <input
                                id="correction-page"
                                data-testid="correction-page-input"
                                type="text"
                                required
                                value={form.page}
                                onChange={update("page")}
                                placeholder="e.g. Zainab Azim profile"
                                className="mt-2 w-full border border-border bg-white px-4 py-3 text-sm text-navy outline-none focus:border-teal"
                            />
                        </div>
                        <div>
                            <label htmlFor="correction-details" className="text-xs font-semibold uppercase tracking-widest text-navy">
                                What is incorrect? <span className="text-teal">*</span>
                            </label>
                            <textarea
                                id="correction-details"
                                data-testid="correction-details-input"
                                required
                                rows={6}
                                value={form.details}
                                onChange={update("details")}
                                placeholder="Describe the error and, if possible, point to the correct information."
                                className="mt-2 w-full border border-border bg-white px-4 py-3 text-sm text-navy outline-none focus:border-teal"
                            />
                        </div>
                        <div className="grid gap-5 sm:grid-cols-2">
                            <div>
                                <label htmlFor="correction-name" className="text-xs font-semibold uppercase tracking-widest text-navy">
                                    Name (optional)
                                </label>
                                <input
                                    id="correction-name"
                                    data-testid="correction-name-input"
                                    type="text"
                                    value={form.name}
                                    onChange={update("name")}
                                    className="mt-2 w-full border border-border bg-white px-4 py-3 text-sm text-navy outline-none focus:border-teal"
                                />
                            </div>
                            <div>
                                <label htmlFor="correction-email" className="text-xs font-semibold uppercase tracking-widest text-navy">
                                    Email (optional)
                                </label>
                                <input
                                    id="correction-email"
                                    data-testid="correction-email-input"
                                    type="email"
                                    value={form.email}
                                    onChange={update("email")}
                                    className="mt-2 w-full border border-border bg-white px-4 py-3 text-sm text-navy outline-none focus:border-teal"
                                />
                            </div>
                        </div>
                        <button
                            type="submit"
                            data-testid="correction-submit-button"
                            disabled={submitting}
                            className="bg-navy px-8 py-3 text-sm font-semibold text-ivory transition-colors hover:bg-teal disabled:opacity-60"
                        >
                            {submitting ? "Submitting…" : "Submit correction"}
                        </button>
                    </form>
                )}
                <p className="mt-10 text-xs text-muted-foreground">
                    For general information about this publication, see{" "}
                    <Link to="/about" data-testid="corrections-about-link" className="font-semibold text-teal link-underline">
                        About &amp; Editorial Standards
                    </Link>
                    .
                </p>
            </section>
        </main>
    );
}
